// ============================================================================
// TAX CALCULATION UTILITIES
// Common utilities for tax calculations across all transaction types
// ============================================================================

import Decimal from "decimal.js";

/**
 * Determine tax scope based on organization and customer details
 */
export const getTaxScope = ({
  organizationCountry,
  organizationState,
  customerCountry,
  placeOfSupply,
  countryMap,
}) => {
  if (!countryMap || !organizationCountry || !organizationState) {
    return "intra";
  }

  const INDIA_ID = countryMap["India"];
  if (!INDIA_ID) {
    return "intra";
  }

  if (organizationCountry !== INDIA_ID || customerCountry !== INDIA_ID) {
    return "single";
  }

  if (!placeOfSupply) {
    return "intra";
  }

  return placeOfSupply === organizationState ? "intra" : "inter";
};

/**
 * Get auto-applied taxes based on variant taxes and tax scope
 */
export const getAutoAppliedTaxes = (variantTaxes, taxScope) => {
  if (!variantTaxes?.length) return [];

  const salesTax = variantTaxes.find((vt) => vt.tax_type === "sales");
  if (!salesTax) return [];

  const appliedTaxes = [];

  if (taxScope === "single" && salesTax.single_tax) {
    appliedTaxes.push({
      value: salesTax.single_tax,
      label: salesTax.single_tax_name,
      isAuto: true,
      type: "single",
    });
  } else if (taxScope === "inter" && salesTax.inter_state_tax) {
    appliedTaxes.push({
      value: salesTax.inter_state_tax,
      label: salesTax.inter_state_tax_name,
      isAuto: true,
      type: "inter",
    });
  } else if (taxScope === "intra" && salesTax.intra_state_tax) {
    appliedTaxes.push({
      value: salesTax.intra_state_tax,
      label: salesTax.intra_state_tax_name,
      isAuto: true,
      type: "intra",
    });
  }

  if (salesTax.additional_tax) {
    appliedTaxes.push({
      value: salesTax.additional_tax,
      label: salesTax.additional_tax_name,
      isAuto: false,
      type: "additional",
    });
  }

  return appliedTaxes;
};

/**
 * Get available tax dropdown options
 */
export const getAvailableTaxOptions = ({
  variantTaxes,
  appliedTaxIds = [],
  taxScope,
}) => {
  if (!variantTaxes?.length) return [];

  const salesTax = variantTaxes.find((vt) => vt.tax_type === "sales");
  if (!salesTax) return [];

  const options = [];

  if (
    taxScope === "single" &&
    salesTax.single_tax &&
    !appliedTaxIds.includes(salesTax.single_tax)
  ) {
    options.push({
      value: salesTax.single_tax,
      label: salesTax.single_tax_name,
      type: "single",
    });
  } else if (
    taxScope === "inter" &&
    salesTax.inter_state_tax &&
    !appliedTaxIds.includes(salesTax.inter_state_tax)
  ) {
    options.push({
      value: salesTax.inter_state_tax,
      label: salesTax.inter_state_tax_name,
      type: "inter",
    });
  } else if (
    taxScope === "intra" &&
    salesTax.intra_state_tax &&
    !appliedTaxIds.includes(salesTax.intra_state_tax)
  ) {
    options.push({
      value: salesTax.intra_state_tax,
      label: salesTax.intra_state_tax_name,
      type: "intra",
    });
  }

  if (
    salesTax.additional_tax &&
    !appliedTaxIds.includes(salesTax.additional_tax)
  ) {
    options.push({
      value: salesTax.additional_tax,
      label: salesTax.additional_tax_name,
      type: "additional",
    });
  }

  return options;
};

/**
 * Calculate tax amount for a line item
 */
export const calculateLineTaxAmount = (
  lineSubtotal,
  taxObjects,
  availableTaxes,
) => {
  if (!taxObjects || !availableTaxes) {
    return { total_tax_amount: 0, tax_breakdown: [] };
  }

  const taxArray = Array.isArray(taxObjects) ? taxObjects : [taxObjects];

  let totalTaxAmount = 0;
  const taxBreakdown = [];

  taxArray.forEach((taxItem) => {
    const taxId = taxItem?.value || taxItem?.id || taxItem;

    let selectedTax = availableTaxes.find(
      (tax) => tax.id === taxId || tax.value === taxId,
    );

    if (!selectedTax && taxItem?.rate) {
      selectedTax = taxItem;
    }

    if (!selectedTax) return;

    const rate = parseFloat(selectedTax.rate) || 0;
    const type = selectedTax.type || "Percentage";

    const amount = type === "Percentage" ? (lineSubtotal * rate) / 100 : rate;

    totalTaxAmount += amount;
    taxBreakdown.push({ tax: taxId, amount });
  });

  return {
    total_tax_amount: parseFloat(totalTaxAmount.toFixed(3)),
    tax_breakdown: taxBreakdown,
  };
};

/**
 * Calculate line subtotal with discount
 */
export const calculateLineSubtotal = ({
  quantity,
  unitPrice,
  discount = 0,
  discountType = "percentage",
  taxes,
  availableTaxes,
  uomOptions = null,
  selectedUomId = null,
}) => {
  const qty =
    uomOptions && selectedUomId
      ? convertQtyToBase(quantity, uomOptions, selectedUomId)
      : parseFloat(quantity) || 0;
  const price = parseFloat(unitPrice) || 0;
  const discountValue = parseFloat(discount) || 0;

  if (qty === 0) {
    return { subtotal: 0, tax_amount: 0, tax_breakdown: [] };
  }

  let discountedUnitPrice = price;

  if (discountType === "percentage") {
    discountedUnitPrice -= (price * discountValue) / 100;
  } else if (discountType === "fixed") {
    discountedUnitPrice -= discountValue;
  }

  discountedUnitPrice = Math.max(discountedUnitPrice, 0);
  const lineSubtotal = discountedUnitPrice * qty;

  const { total_tax_amount, tax_breakdown } = calculateLineTaxAmount(
    lineSubtotal,
    taxes,
    availableTaxes,
  );

  return {
    subtotal: parseFloat(lineSubtotal.toFixed(3)),
    tax_amount: total_tax_amount,
    tax_breakdown,
  };
};

/**
 * Calculate document totals
 */
export const calculateDocumentTotals = ({
  products,
  globalDiscount = 0,
  globalDiscountType = "percentage",
  adjustmentAmount = 0,
  adjustmentType = "+",
  availableTaxes,
}) => {
  const untaxedTotal = products.reduce(
    (sum, p) => sum + (parseFloat(p.subtotal) || 0),
    0,
  );

  const discountAmount =
    globalDiscountType === "percentage"
      ? untaxedTotal * (globalDiscount / 100)
      : globalDiscount;

  let totalTax = 0;

  products.forEach((p) => {
    const ratio = untaxedTotal ? p.subtotal / untaxedTotal : 0;
    const taxable = p.subtotal - discountAmount * ratio;

    const { total_tax_amount } = calculateLineTaxAmount(
      taxable,
      p.taxes || p.tax,
      availableTaxes,
    );

    totalTax += total_tax_amount;
  });

  const finalUntaxed = untaxedTotal - discountAmount;
  const total = finalUntaxed + totalTax;
  const net =
    adjustmentType === "+"
      ? total + adjustmentAmount
      : total - adjustmentAmount;

  return {
    gross_total: untaxedTotal,
    untaxed_total: finalUntaxed,
    tax_amount: totalTax,
    total_amount: total,
    net_total_amount: net,
    discount_amount: discountAmount,
  };
};

/**
 * Calculate profit
 */
export const calculateProfit = (products) => {
  let totalProfit = 0;

  products.forEach((p) => {
    totalProfit +=
      (parseFloat(p.subtotal) || 0) -
      (parseFloat(p.cost_price) || 0) * (parseInt(p.quantity) || 0);
  });

  return parseFloat(totalProfit.toFixed(3));
};

/**
 * Parse formatted numbers
 */
export const parseFormattedNumber = (value) => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  return parseFloat(String(value).replace(/[^0-9.-]/g, "")) || 0;
};

/**
 * Decimal safe numeric value
 */
export const getNumericValue = (value, decimals = 2) => {
  return new Decimal(parseFormattedNumber(value))
    .toDecimalPlaces(decimals)
    .toNumber();
};

/**
 * Normalize address
 */
export const normalizeAddress = (address) => {
  if (!address) return null;
  return {
    ...address,
    country:
      typeof address.country === "object"
        ? address.country.id
        : address.country,
    state: typeof address.state === "object" ? address.state.id : address.state,
  };
};

/**
 * Get existing tax scope
 */
export const getExistingTaxScope = (taxes) => {
  return taxes?.[0]?.type || null;
};

/**
 * Update taxes when scope changes
 */
export const updateProductTaxesOnScopeChange = ({
  products,
  newScope,
  getAutoAppliedTaxesFn,
  availableTaxes,
}) => {
  return products.map((product) => {
    if (!product.variant_taxes) return product;

    const oldScope = getExistingTaxScope(product.taxes);
    if (oldScope === newScope) return product;

    const taxes = getAutoAppliedTaxesFn(product.variant_taxes, newScope);
    const { total_tax_amount, tax_breakdown } = calculateLineTaxAmount(
      product.subtotal,
      taxes,
      availableTaxes,
    );

    return {
      ...product,
      taxes,
      tax_amount: total_tax_amount,
      tax_breakdown,
    };
  });
};

export const convertQtyToBase = (qty, uomOptions, selectedUomId) => {
  if (!uomOptions || !selectedUomId) return parseFloat(qty) || 0;

  const uom = uomOptions.find(
    (u) => u.value === selectedUomId || u.value === parseInt(selectedUomId),
  );
  if (
    !uom ||
    uom.is_base_uom ||
    !uom.conversion_factor ||
    uom.conversion_factor === 0
  ) {
    return parseFloat(qty) || 0;
  }

  return (parseFloat(qty) || 0) * parseFloat(uom.conversion_factor);
};
